import React from 'react';
import { Globe } from 'lucide-react';
import { clsx } from 'clsx';
import type { KnownCluster } from '@/utils/clusterReconcile';

export const mentionOptionId = (listId: string, index: number) => `${listId}-opt-${index}`;

interface MentionSuggestionsProps {
  /** id the chat input points at with aria-controls */
  id: string;
  /** accessible name of the list */
  label: string;
  options: KnownCluster[];
  /** index of the highlighted option (the input's aria-activedescendant) */
  activeIndex: number;
  onSelect: (cluster: KnownCluster) => void;
  onHover: (index: number) => void;
}

/**
 * STORY-300: the @-mention suggestion list under the chat input. Keyboard
 * focus stays in the textarea (combobox pattern): the input owns ArrowUp/Down,
 * Enter/Tab and Esc, and points at the highlighted option with
 * aria-activedescendant. A mouse pick uses onMouseDown + preventDefault so the
 * textarea keeps focus and its caret.
 */
export const MentionSuggestions: React.FC<MentionSuggestionsProps> = ({
  id, label, options, activeIndex, onSelect, onHover,
}) => (
  <ul
    id={id}
    role="listbox"
    aria-label={label}
    className="absolute bottom-full left-0 mb-1 z-20 max-h-48 w-64 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 py-1 shadow-lg"
  >
    {options.map((c, i) => (
      <li
        key={c.id}
        id={mentionOptionId(id, i)}
        role="option"
        aria-selected={i === activeIndex}
        onMouseDown={(e) => {
          e.preventDefault();
          onSelect(c);
        }}
        onMouseEnter={() => onHover(i)}
        className={clsx(
          'flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm',
          i === activeIndex
            ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200'
            : 'text-gray-800 dark:text-gray-100',
        )}
      >
        <Globe className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span className="font-mono">@{c.slug}</span>
        {c.name !== c.slug && (
          <span className="truncate text-xs text-gray-500 dark:text-gray-400">{c.name}</span>
        )}
      </li>
    ))}
  </ul>
);
